/**
 * Portal door transition sound — synthesised via WebAudio.
 *
 * Ships ZERO audio bytes (well under the 200KB budget). Composed of:
 *   1. Bass thud (60Hz sine + 90Hz sine) — the heavy wooden weight
 *   2. Wood-body impact (filtered noise burst) — the door hitting the frame
 *   3. Metallic latch click (short high resonance)
 *   4. Rising whoosh (band-pass filtered noise sweeping 180Hz -> 2200Hz)
 *
 * Total duration ~2.0s, mixed at ~45% peak gain. Only plays on user
 * gesture (the door click) — never on page load.
 */

const MUTE_KEY = "aestherium_portal_audio_muted";

export function isPortalAudioMuted() {
    if (typeof window === "undefined") return false;
    try {
        return window.sessionStorage.getItem(MUTE_KEY) === "1";
    } catch {
        return false;
    }
}

export function setPortalAudioMuted(muted) {
    if (typeof window === "undefined") return;
    try {
        window.sessionStorage.setItem(MUTE_KEY, muted ? "1" : "0");
    } catch {
        /* private mode etc — ignore */
    }
}

function isReducedMotion() {
    return (
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    );
}

let cachedCtx = null;
/**
 * Reuse a single AudioContext per session. Browsers cap the number of
 * AudioContexts an origin can hold; creating one per click leaks.
 * Must be lazy-instantiated on a user gesture (first door click) so it
 * starts in "running" state.
 */
function getCtx() {
    if (typeof window === "undefined") return null;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!cachedCtx || cachedCtx.state === "closed") {
        try {
            cachedCtx = new AudioCtx();
        } catch {
            return null;
        }
    }
    if (cachedCtx.state === "suspended") {
        cachedCtx.resume().catch(() => {});
    }
    return cachedCtx;
}

/**
 * Play the door-transition sound. Returns immediately; sound plays async.
 * Silently no-ops if muted / reduced-motion / no WebAudio support.
 */
export function playDoorTransition() {
    if (isPortalAudioMuted()) return;
    if (isReducedMotion()) return;

    const ctx = getCtx();
    if (!ctx) return;

    const t0 = ctx.currentTime;

    // Master bus @ 0.45 peak so overall volume sits in the 40-50% target.
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.001, t0);
    master.gain.exponentialRampToValueAtTime(0.45, t0 + 0.04);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + 2.2);
    master.connect(ctx.destination);

    // ---------- 1. Bass thud (heavy wooden weight, ~60Hz + 90Hz) ----------
    [60, 92].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, t0);
        osc.frequency.exponentialRampToValueAtTime(freq * 0.55, t0 + 0.9);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(i === 0 ? 0.9 : 0.5, t0 + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.1);
        osc.connect(g).connect(master);
        osc.start(t0);
        osc.stop(t0 + 1.2);
    });

    // ---------- 2. Wood-body impact (filtered noise) ----------
    const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 2.2, ctx.sampleRate);
    const noiseData = noiseBuf.getChannelData(0);
    for (let i = 0; i < noiseData.length; i++) {
        noiseData[i] = (Math.random() * 2 - 1) * 0.7;
    }

    const woodNoise = ctx.createBufferSource();
    woodNoise.buffer = noiseBuf;
    const woodFilter = ctx.createBiquadFilter();
    woodFilter.type = "lowpass";
    woodFilter.frequency.setValueAtTime(320, t0);
    woodFilter.Q.value = 4;
    const woodGain = ctx.createGain();
    woodGain.gain.setValueAtTime(0.0001, t0);
    woodGain.gain.exponentialRampToValueAtTime(0.55, t0 + 0.02);
    woodGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.5);
    woodNoise.connect(woodFilter).connect(woodGain).connect(master);
    woodNoise.start(t0);
    woodNoise.stop(t0 + 0.6);

    // ---------- 3. Metallic latch click ----------
    const clickOsc = ctx.createOscillator();
    clickOsc.type = "triangle";
    clickOsc.frequency.setValueAtTime(1800, t0);
    clickOsc.frequency.exponentialRampToValueAtTime(600, t0 + 0.08);
    const clickGain = ctx.createGain();
    clickGain.gain.setValueAtTime(0.0001, t0);
    clickGain.gain.exponentialRampToValueAtTime(0.25, t0 + 0.006);
    clickGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.11);
    clickOsc.connect(clickGain).connect(master);
    clickOsc.start(t0);
    clickOsc.stop(t0 + 0.12);

    // ---------- 4. Rising whoosh (sweeps 180 -> 2200Hz over 1.2s) ----------
    const whooshNoise = ctx.createBufferSource();
    whooshNoise.buffer = noiseBuf;
    const whooshFilter = ctx.createBiquadFilter();
    whooshFilter.type = "bandpass";
    whooshFilter.Q.value = 2;
    whooshFilter.frequency.setValueAtTime(180, t0 + 0.4);
    whooshFilter.frequency.exponentialRampToValueAtTime(2200, t0 + 1.7);
    const whooshGain = ctx.createGain();
    whooshGain.gain.setValueAtTime(0.0001, t0 + 0.4);
    whooshGain.gain.exponentialRampToValueAtTime(0.35, t0 + 0.9);
    whooshGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.9);
    whooshNoise.connect(whooshFilter).connect(whooshGain).connect(master);
    whooshNoise.start(t0 + 0.4);
    whooshNoise.stop(t0 + 2.0);
}

/**
 * Warm chime — kept here so it can share the same mute preference + ctx.
 */
export function playWarmChime() {
    if (isPortalAudioMuted()) return;
    if (isReducedMotion()) return;
    const ctx = getCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(0.06, now + 0.06);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 2.4);
    master.connect(ctx.destination);
    [
        { freq: 523.25, gain: 0.9 },
        { freq: 783.99, gain: 0.35 },
        { freq: 261.63, gain: 0.5 },
    ].forEach((p, i) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(p.freq, now);
        const g = ctx.createGain();
        g.gain.value = p.gain;
        osc.connect(g).connect(master);
        osc.start(now + i * 0.04);
        osc.stop(now + 2.6);
    });
}
